import React from "react";

const HealthDialog = ({
  open,
  onClose,
  styles, // kept for compatibility; we override with theme but allow merge

  // form mode
  isManual,
  setIsManual,
  isDigital,
  setIsDigital,

  // OTP state + handlers
  otpVerified,
  otpSent,
  resendTimer,
  error,
  phoneNumber,
  onSendOTP,
  onVerifyOTP,
  onResendOTP,
  onOtpChange,

  // health questions
  healthQuestion,
  responses,
  handleResponseChange,

  // manual/single long form gate
  singleManualForm,

  // file uploads
  imagePreviews,
  imageErrors,
  handleFileChange,
  handleRemoveImage,

  // links
  pdfDownloadLink,
  memmberShipFormLink,

  // save-guard
  isHealthFormValid,
  onSave,
}) => {
  if (!open) return null;

  // ---- THEME (Flashaid light: yellow + white, Josefin Sans) ----
  const theme = {
    font: '"Josefin Sans", sans-serif',
    yellow: "#facc15",
    yellowHover: "#fcd34d",
    border: "#f5e68d",
    text: "#333",
    subtext: "#555",
    danger: "#ef4444",
  };

  // Base theme styles (merged with any incoming styles prop; theme wins)
  const s = {
    dialogOverlay: {
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      ...(styles?.dialogOverlay || {}),
    },
    dialog: {
      backgroundColor: "#ffffff",
      border: `1px solid ${theme.border}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      borderRadius: 12,
      width: "min(620px, 92vw)",
      maxHeight: "86vh",
      overflowY: "auto",
      padding: "18px 18px 14px",
      fontFamily: theme.font,
      color: theme.text,
      ...(styles?.dialog || {}),
    },
    dialogTitle: {
      fontFamily: theme.font,
      fontWeight: 700,
      fontSize: "1.2rem",
      margin: "0 0 8px",
      color: theme.text,
      ...(styles?.dialogTitle || {}),
    },
    formSelection: {
      display: "flex",
      gap: 8,
      marginBottom: 10,
      ...(styles?.formSelection || {}),
    },
    buttonBase: {
      fontFamily: theme.font,
      borderRadius: 8,
      padding: "10px 14px",
      fontWeight: 600,
      border: "none",
      cursor: "pointer",
      transition: "background-color .2s ease, transform .05s ease",
    },
    buttonPrimary: {
      backgroundColor: theme.yellow,
      color: theme.text,
      boxShadow: "0 2px 6px rgba(250,204,21,0.35)",
    },
    buttonPrimaryHover: {
      backgroundColor: theme.yellowHover,
    },
    buttonGhost: {
      backgroundColor: "#fff",
      color: theme.text,
      border: `1px solid ${theme.border}`,
    },
    otpCard: {
      background: "#fff",
      border: `1px solid ${theme.border}`,
      borderRadius: 10,
      padding: 14,
      marginTop: 4,
      maxWidth: 420,
      marginInline: "auto",
    },
    input: {
      width: "100%",
      fontFamily: theme.font,
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      outline: "none",
      marginBottom: 6, // small gap under input
    },
    inputDisabled: {
      background: "#f7f7f7",
      color: "#777",
    },
    btnFull: {
      width: "100%",
      marginTop: 6,
    },
    link: {
      color: "#1d4ed8",
      textDecoration: "underline",
      display: "inline-block",
      margin: "8px 0 6px",
      fontFamily: theme.font,
    },
    dialogContent: {
      marginTop: 8,
      ...(styles?.dialogContent || {}),
    },
    question: {
      marginBottom: 10, // small gaps between Qs
      ...(styles?.question || {}),
    },
    label: {
      display: "block",
      fontWeight: 600,
      color: theme.text,
      marginBottom: 4, // small gap label->radios
      ...(styles?.label || {}),
    },
    radioGroup: {
      display: "flex",
      gap: 12,
      ...(styles?.radioGroup || {}),
    },
    radioLabel: {
      cursor: "pointer",
      color: theme.subtext,
      ...(styles?.radioLabel || {}),
    },
    pdfSection: {
      marginTop: 8,
      ...(styles?.pdfSection || {}),
    },
    footer: {
      marginTop: 12,
      display: "flex",
      justifyContent: "space-between",
      gap: 8,
      ...(styles?.footer || {}),
    },
    buttonCancel: {
      fontFamily: theme.font,
      borderRadius: 8,
      padding: "10px 14px",
      fontWeight: 600,
      backgroundColor: "#fff",
      color: theme.text,
      border: `1px solid ${theme.border}`,
      cursor: "pointer",
    },
    buttonSave: {
      fontFamily: theme.font,
      borderRadius: 8,
      padding: "10px 14px",
      fontWeight: 700,
      backgroundColor: theme.yellow,
      color: theme.text,
      border: "none",
      boxShadow: "0 2px 6px rgba(250,204,21,0.35)",
      cursor: "pointer",
    },
    helper: {
      fontSize: ".92rem",
      color: theme.subtext,
      margin: "0 0 8px",
    },
    error: {
      color: theme.danger,
      marginTop: 2,
      marginBottom: 8,
      fontSize: ".92rem",
      display: "block",
    },
    closeX: {
      position: "absolute",
      top: 6,
      right: 10,
      fontSize: 22,
      lineHeight: 1,
      background: "transparent",
      border: "none",
      color: "#777",
      cursor: "pointer",
    },
  };

  // tiny hover helper
  const onHover = (e) => (e.currentTarget.style.backgroundColor = s.buttonPrimaryHover.backgroundColor);
  const onLeave = (e) => (e.currentTarget.style.backgroundColor = s.buttonPrimary.backgroundColor);

  return (
    <div style={s.dialogOverlay} onClick={onClose}>
      <div style={s.dialog} onClick={(e) => e.stopPropagation()}>
        <button style={s.closeX} onClick={onClose} aria-label="Close">×</button>

        <h2 style={s.dialogTitle}>Health Questionnaire</h2>

        {/* Selection for Form Type */}
        <div style={s.formSelection}>
          <button
            type="button"
            onClick={() => setIsManual(true)}
            style={{ ...s.buttonBase, ...s.buttonGhost }}
          >
            Manual Form
          </button>

          <button
            type="button"
            onClick={() => {
              setIsDigital(true);
              setIsManual(false);
            }}
            style={{ ...s.buttonBase, ...s.buttonPrimary }}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
          >
            Digital Verification
          </button>
        </div>

        {/* Manual Form PDF Download */}
        {isManual && !singleManualForm && (
          <div style={s.pdfSection}>
            <p style={s.helper}><strong>Instructions to fill out the manual forms:</strong></p>
            <p style={s.helper}><strong>Step 1:</strong> Fill out the membership form first, which comprises 4 questions in the Declaration of Good Health (DOGH).</p>
            <p style={s.helper}><strong>Step 2:</strong></p>
            <p style={s.helper}><strong>A.</strong> If you mark "No" to all 4 questions, submit only the membership form.</p>
            <p style={s.helper}><strong>B.</strong> If you mark "Yes" to one or more questions in the Declaration of Good Health (DOGH) of the membership form, it will be mandatory to attach the Medical Questionnaire (Long Form).</p>

            <p style={s.helper}><strong>फॉर्म भरण्याच्या सूचना:</strong></p>
            <p style={s.helper}><strong>चरण १:</strong> कृपया सर्वप्रथम सदस्यत्व फॉर्म (membership form) भरा, ज्यामध्ये आरोग्याच्या घोषणापत्रात ४ प्रश्नांचा समावेश आहे.</p>
            <p style={s.helper}><strong>चरण २:</strong></p>
            <p style={s.helper}><strong>अ.</strong> जर तुम्ही सर्व ४ प्रश्नांना "नाही" असे चिन्हांकित केले असेल, तर केवळ सदस्यत्व फॉर्म (membership form) जमा करा.</p>
            <p style={s.helper}><strong>ब.</strong> जर तुम्ही सदस्यत्व फॉर्मच्या (membership form) आरोग्याच्या घोषणापत्रात एक किंवा अधिक प्रश्नांना "होय" असे चिन्हांकित केले असेल, तर वैद्यकीय प्रश्नावली (लाँग फॉर्म) संलग्न करणे बंधनकारक आहे.</p>

            <a href={memmberShipFormLink} download style={s.link} target="blank">
              Download Membership Question Form Here
            </a>

            <label style={{ display: "block", marginTop: 6 }}>
              Membership Question Form:
              <input
                type="file"
                name="membership"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ ...s.input, marginTop: 4 }}
              />
              {imagePreviews.membership && (
                <div className="image-preview" style={{ marginTop: 8 }}>
                  <img src={imagePreviews.membership} alt="Member quest" />
                  <p>File uploaded</p>
                  <button type="button" onClick={() => handleRemoveImage("membership")} style={{ ...s.buttonBase, ...s.buttonGhost, marginTop: 6 }}>
                    Change File
                  </button>
                </div>
              )}
              {imageErrors.membership && <span style={s.error}>{imageErrors.membership}</span>}
            </label>

            <a href={pdfDownloadLink} download style={s.link} target="blank">
              Download Long Question Form Here
            </a>

            <label style={{ display: "block", marginTop: 6 }}>
              Long Question Form:
              <input
                type="file"
                name="longQuestionnaire"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ ...s.input, marginTop: 4 }}
              />
              {imagePreviews.longQuestionnaire && (
                <div className="image-preview" style={{ marginTop: 8 }}>
                  <img src={imagePreviews.longQuestionnaire} alt="Long quest" />
                  <p>File uploaded</p>
                  <button type="button" onClick={() => handleRemoveImage("longQuestionnaire")} style={{ ...s.buttonBase, ...s.buttonGhost, marginTop: 6 }}>
                    Change File
                  </button>
                </div>
              )}
              {imageErrors.longQuestionnaire && <span style={s.error}>{imageErrors.longQuestionnaire}</span>}
            </label>
          </div>
        )}

        {/* Digital Verification - OTP Section */}
        {isDigital && !otpVerified && (
          <div style={s.otpCard}>
            <h3 style={{ margin: "0 0 8px", fontFamily: theme.font, fontSize: "1.05rem" }}>OTP Verification</h3>

            <input
              disabled
              type="text"
              placeholder="Enter 10-digit phone number"
              value={phoneNumber}
              maxLength="10"
              style={{ ...s.input, ...s.inputDisabled }}
            />
            <button
              type="button"
              onClick={onSendOTP}
              disabled={phoneNumber.length !== 10}
              style={{
                ...s.buttonBase,
                ...s.buttonPrimary,
                ...s.btnFull,
                backgroundColor: phoneNumber.length === 10 ? s.buttonPrimary.backgroundColor : "#e5e7eb",
                color: phoneNumber.length === 10 ? s.buttonPrimary.color : "#777",
                boxShadow: phoneNumber.length === 10 ? s.buttonPrimary.boxShadow : "none",
                cursor: phoneNumber.length === 10 ? "pointer" : "not-allowed",
              }}
              onMouseEnter={(e) => phoneNumber.length === 10 && onHover(e)}
              onMouseLeave={(e) => phoneNumber.length === 10 && onLeave(e)}
            >
              {otpSent ? "OTP Sent" : "Send OTP"}
            </button>

            {otpSent && (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  onChange={onOtpChange}
                  maxLength="6"
                  style={{ ...s.input, marginTop: 6 }}
                />
                <button
                  type="button"
                  onClick={onVerifyOTP}
                  style={{ ...s.buttonBase, ...s.buttonPrimary, ...s.btnFull }}
                  onMouseEnter={onHover}
                  onMouseLeave={onLeave}
                >
                  Verify OTP
                </button>

                <button
                  type="button"
                  onClick={onResendOTP}
                  disabled={resendTimer > 0}
                  style={{
                    ...s.buttonBase,
                    ...s.btnFull,
                    backgroundColor: resendTimer === 0 ? theme.yellow : "#e5e7eb",
                    color: resendTimer === 0 ? theme.text : "#777",
                    border: `1px solid ${resendTimer === 0 ? theme.border : "#e5e7eb"}`,
                    marginTop: 8,
                    cursor: resendTimer === 0 ? "pointer" : "not-allowed",
                  }}
                >
                  {resendTimer === 0 ? "Resend OTP" : `Resend OTP in ${resendTimer}s`}
                </button>
              </>
            )}

            {error && <p style={{ ...s.error, marginBottom: 0 }}>{error}</p>}
          </div>
        )}

        {/* Health Questions (only if Digital Verification is chosen) */}
        {isDigital && otpVerified && (
          <div style={s.dialogContent}>
            {healthQuestion.map((question) => (
              <div key={question.key} style={s.question}>
                <label style={s.label}>{question.label}</label>
                <div style={s.radioGroup}>
                  <label style={s.radioLabel}>
                    <input
                      type="radio"
                      name={question.key}
                      value="yes"
                      checked={responses[question.key] === "yes"}
                      onChange={() => handleResponseChange(question.key, "yes")}
                      style={{ marginRight: 6 }}
                    />
                    Yes
                  </label>
                  <label style={s.radioLabel}>
                    <input
                      type="radio"
                      name={question.key}
                      value="no"
                      checked={responses[question.key] === "no"}
                      onChange={() => handleResponseChange(question.key, "no")}
                      style={{ marginRight: 6 }}
                    />
                    No
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Single Manual Long Form (shown when any Yes in DOGH) */}
        {singleManualForm && (
          <div style={s.pdfSection}>
            <p style={s.helper}>
              <b>
                <i>
                  You have selected "Yes" to one or more questions in the Declaration of Good Health
                  (DOGH). As a result, it is mandatory to complete the Medical Questionnaire (Long
                  Form). Please download the Medical Questionnaire (Long Form) from the provided
                  link, fill it out, and upload the completed form to proceed.
                </i>
              </b>
            </p>

            <a href={pdfDownloadLink} download style={s.link} target="blank">
              Download PDF
            </a>

            <label style={{ display: "block", marginTop: 6 }}>
              Long Question Form:
              <input
                type="file"
                name="longQuestionnaire"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ ...s.input, marginTop: 4 }}
              />
              {imagePreviews.longQuestionnaire && (
                <div className="image-preview" style={{ marginTop: 8 }}>
                  <img src={imagePreviews.longQuestionnaire} alt="Long quest" />
                  <p>File uploaded</p>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage("longQuestionnaire")}
                    style={{ ...s.buttonBase, ...s.buttonGhost, marginTop: 6 }}
                  >
                    Change File
                  </button>
                </div>
              )}
              {imageErrors.longQuestionnaire && <span style={s.error}>{imageErrors.longQuestionnaire}</span>}
            </label>
          </div>
        )}

        {/* Footer Actions */}
        <div style={s.footer}>
          <button type="button" onClick={onClose} style={s.buttonCancel}>
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={!isHealthFormValid}
            style={{
              ...s.buttonSave,
              backgroundColor: isHealthFormValid ? theme.yellow : "#e5e7eb",
              color: isHealthFormValid ? theme.text : "#777",
              boxShadow: isHealthFormValid ? "0 2px 6px rgba(250,204,21,0.35)" : "none",
              cursor: isHealthFormValid ? "pointer" : "not-allowed",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthDialog;
